/**
 * 북BTI — 어떤 독자인가.
 *
 * MBTI 16 유형을 따라가지 않는다. 네 글자는 MUROO 안에서 아무 뜻이 없고,
 * 'INFJ형 독서가' 는 결과를 받아도 행성으로 이어지지 않는다. 그래서
 * MUROO 의 말로 된 여덟 유형을 두고, 결과에서 그 사람의 행성·닮은 사람·
 * 함께 읽은 책으로 곧장 잇는다.
 *
 * 축을 세 개 잡아 2×2×2 로 나누지 않는다. '밤의 사색가' 와 '기록하는 수집가'
 * 는 한 축의 양끝이 아니라 서로 다른 결이어서, 억지로 나누면 아무도 자기
 * 얘기라고 느끼지 않는 칸이 생긴다. 대신 고를 때마다 유형에 점수를 더하고
 * 가장 높은 유형을 고른다. 같으면 TIE_ORDER 순서로 정한다 — 같은 답이면
 * 늘 같은 결과가 나와야 한다.
 */

export const TYPES = {
  night: {
    key: "night", icon: "🌙", name: "밤의 사색가",
    lead: "혼자 오래 생각할 수 있는 이야기에 끌려요.",
    body: "사람의 마음과 관계를 들여다보는 책을 좋아하고, 한 권을 덮고도 그 안에 오래 머무는 편이에요.",
  },
  sentence: {
    key: "sentence", icon: "🔭", name: "문장 탐험가",
    lead: "이야기보다 한 문장에 먼저 붙잡혀요.",
    body: "좋은 문장을 만나면 그 자리에서 옮겨 적고, 나중에 그 문장 때문에 책 전체를 다시 펼쳐요.",
  },
  slow: {
    key: "slow", icon: "🌱", name: "천천히 자라는 독자",
    lead: "매일 조금씩, 꾸준히 읽어요.",
    body: "한 번에 몰아 읽기보다 여러 권을 곁에 두고 오래 갑니다. 읽은 만큼 자기 자리가 넓어지는 쪽이에요.",
  },
  chaser: {
    key: "chaser", icon: "⚡", name: "새로운 별을 쫓는 독자",
    lead: "지금 막 떠오른 책이 궁금해요.",
    body: "화제가 된 책, 처음 보는 분야로 계속 옮겨 다녀요. 한 자리에 오래 머무르기보다 다음 별로 갑니다.",
  },
  keeper: {
    key: "keeper", icon: "📜", name: "기록하는 수집가",
    lead: "읽고 나면 남겨야 끝이 나요.",
    body: "읽은 목록이 쌓이는 걸 좋아하고, 기록해 두면 그 책이 오래 남는다는 걸 알고 있어요.",
  },
  voyager: {
    key: "voyager", icon: "🪐", name: "세계관 여행자",
    lead: "낯선 세계에 오래 머무는 걸 좋아해요.",
    body: "두꺼운 책도 시리즈도 반갑습니다. 이야기의 규칙과 지도를 익히는 시간 자체가 즐거운 쪽이에요.",
  },
  feeler: {
    key: "feeler", icon: "💫", name: "감정으로 읽는 사람",
    lead: "그날의 마음이 책을 고릅니다.",
    body: "줄거리보다 남은 기분으로 책을 기억해요. 좋았던 책을 떠올릴 때 장면보다 감정이 먼저 옵니다.",
  },
  diver: {
    key: "diver", icon: "🔥", name: "한 권에 몰입하는 사람",
    lead: "한 번 잡으면 끝까지 갑니다.",
    body: "읽는 동안은 그 책만 있어요. 밤을 새워서라도 마지막 장을 보고서야 다음으로 넘어갑니다.",
  },
};

/** 점수가 같을 때의 순서. 같은 답이면 늘 같은 결과가 나와야 한다. */
export const TIE_ORDER = ["night", "sentence", "feeler", "voyager", "diver", "keeper", "slow", "chaser"];

/**
 * 질문 8개. 고르는 방식 → 읽는 방식 → 남기는 것 순으로 간다.
 * 고른 답 하나가 두 유형에 점수를 준다(2점·1점). 한 유형만 올리면
 * 네 번째 질문쯤에서 결과가 정해져 나머지가 무의미해진다.
 */
export const QUESTIONS = [
  {
    id: "pick",
    q: "서점에 들어섰습니다. 발이 먼저 향하는 곳은?",
    options: [
      { t: "요즘 사람들이 많이 읽는 매대", s: { chaser: 2, slow: 1 } },
      { t: "오래된 소설이 꽂힌 서가", s: { night: 2, feeler: 1 } },
      { t: "두꺼운 시리즈가 줄지어 선 칸", s: { voyager: 2, diver: 1 } },
      { t: "시집과 산문이 모인 구석", s: { sentence: 2, feeler: 1 } },
    ],
  },
  {
    id: "first",
    q: "책을 집어 들고 가장 먼저 확인하는 것은?",
    options: [
      { t: "아무 쪽이나 펼쳐 문장 몇 줄", s: { sentence: 2, night: 1 } },
      { t: "표지와 제목이 주는 분위기", s: { feeler: 2, night: 1 } },
      { t: "목차와 저자 소개", s: { slow: 2, keeper: 1 } },
      { t: "어떤 세계가 배경인지", s: { voyager: 2, chaser: 1 } },
    ],
  },
  {
    id: "pace",
    q: "읽는 속도는 어느 쪽에 가까운가요?",
    options: [
      { t: "잡으면 끝까지, 밤을 새워서라도", s: { diver: 2, voyager: 1 } },
      { t: "밤마다 조금씩, 오래 두고", s: { night: 2, sentence: 1 } },
      { t: "여러 권을 곁에 두고 번갈아", s: { slow: 2, chaser: 1 } },
      { t: "마음이 끌릴 때 몰아서", s: { feeler: 2, diver: 1 } },
    ],
  },
  {
    id: "mark",
    q: "읽다가 마음에 걸리는 문장을 만나면?",
    options: [
      { t: "그 자리에서 옮겨 적어요", s: { sentence: 2, keeper: 1 } },
      { t: "잠시 덮고 생각해요", s: { night: 2, feeler: 1 } },
      { t: "기억해 둘 정보라면 표시해요", s: { keeper: 2, slow: 1 } },
      { t: "흐름이 끊기니 그냥 읽어 내려가요", s: { diver: 2, chaser: 1 } },
    ],
  },
  {
    id: "stuck",
    q: "읽다가 잘 넘어가지 않는 책이 있다면?",
    options: [
      { t: "그래도 끝까지 붙잡아요", s: { diver: 2, keeper: 1 } },
      { t: "덮어 두고 다른 책으로", s: { chaser: 2, feeler: 1 } },
      { t: "천천히, 며칠에 걸쳐 다시", s: { slow: 2, night: 1 } },
      { t: "세계가 열릴 때까지 버텨요", s: { voyager: 2, diver: 1 } },
    ],
  },
  {
    id: "after",
    q: "다 읽고 책을 덮은 직후에 하는 일은?",
    options: [
      { t: "읽은 기록을 남겨 정리해요", s: { keeper: 2, slow: 1 } },
      { t: "문장 하나를 오래 곱씹어요", s: { sentence: 2, night: 1 } },
      { t: "남은 기분에 한참 잠겨 있어요", s: { feeler: 2, night: 1 } },
      { t: "바로 다음 책을 찾아요", s: { chaser: 2, diver: 1 } },
    ],
  },
  {
    id: "memory",
    q: "한참 지난 뒤, 그 책의 무엇이 남아 있나요?",
    options: [
      { t: "그때 내 마음이 어땠는지", s: { feeler: 2, sentence: 1 } },
      { t: "인물이 한 선택과 그 이유", s: { night: 2, voyager: 1 } },
      { t: "그 세계의 지도와 규칙", s: { voyager: 2, keeper: 1 } },
      { t: "알게 된 사실과 배운 것", s: { slow: 2, keeper: 1 } },
    ],
  },
  {
    id: "shelf",
    q: "당신의 책장은 어떤 모습에 가깝나요?",
    options: [
      { t: "읽은 목록과 기록이 차곡차곡", s: { keeper: 2, slow: 1 } },
      { t: "밑줄과 접힌 자국이 가득", s: { sentence: 2, diver: 1 } },
      { t: "한 작가·한 세계가 줄지어", s: { voyager: 2, night: 1 } },
      { t: "그때그때 끌린 책이 뒤섞여", s: { chaser: 2, feeler: 1 } },
    ],
  },
];

/**
 * 고른 답(질문 id → 고른 번호)으로 유형을 정한다.
 * @returns {{ key, score, runnerUp }}
 */
export function scoreAnswers(answers) {
  const sum = {};
  for (const q of QUESTIONS) {
    const pick = q.options[answers?.[q.id]];
    if (!pick) continue;
    for (const [k, v] of Object.entries(pick.s)) sum[k] = (sum[k] || 0) + v;
  }
  const ranked = TIE_ORDER
    .map((k) => ({ key: k, score: sum[k] || 0 }))
    .sort((a, b) => b.score - a.score || TIE_ORDER.indexOf(a.key) - TIE_ORDER.indexOf(b.key));
  return { key: ranked[0].key, score: ranked[0].score, runnerUp: ranked[1]?.key };
}
